/* eslint-disable arrow-body-style */
import React, { memo, useMemo, useState } from 'react';
import { useIntl, getLocale } from 'umi';
import { Bubble, Table, TableColumn, PagingQuery } from 'tea-component';
// import SvgIcon from '@/pages/home/components/SvgIcon';
import TableState, {
  TABLE_STATE,
} from '@/pages/intelligence/pages/StoreRanks/MobileGlobalRegion/components/TableState';
import CompareGameBox from '@/pages/intelligence/pages/StoreRanks/MobileGlobalRegion/components/CompareGameBox';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { addImageMogr2Url } from '@/pages/intelligence/utils/commonUtils';
import ARightClickToJump from '@/pages/intelligence/components/ARightClickToJump';
import type { IBaseProps } from '@/pages/intelligence/types/common';
import type {
  IMobileStoreRankTopGlobalCompareRank,
  IMobileStoreRankTopGlobalCompareRankGame,
} from '@/pages/intelligence/types/storeRank';

import './index.scss';

const { pageable, scrollable } = Table.addons;

const handleGames = (
  list: IMobileStoreRankTopGlobalCompareRankGame[],
): IMobileStoreRankTopGlobalCompareRankGame[] => {
  if (!Array.isArray(list)) return [];
  return list.map((item) => {
    const compressedCover = addImageMogr2Url(item?.cover, 96, 96);
    return {
      ...item,
      cover: compressedCover,
    };
  });
};

const handleGameRanks = (
  list: IMobileStoreRankTopGlobalCompareRank[],
): IMobileStoreRankTopGlobalCompareRank[] => {
  if (!Array.isArray(list)) return [];
  return list.map((item) => {
    const games = handleGames(item?.game_rank);
    return {
      ...item,
      game_rank: games,
    };
  });
};

interface IGlobalCompareTableProps extends IBaseProps {
  isLoading: boolean;
  isError: boolean;
  total: number;
  records: IMobileStoreRankTopGlobalCompareRank[];
  pageIndex: number;
  pageSize: number;
  pageSizeOptions?: number[];
  onPagingChange: (payload: PagingQuery) => void;
  onRefresh?: () => void;
  onCountryClick?: (countryCode: string, e: React.SyntheticEvent<HTMLDivElement>) => void;
  onGameClick?: (
    game: IMobileStoreRankTopGlobalCompareRankGame,
    e: React.SyntheticEvent<HTMLDivElement>,
  ) => void;
  getCountryDetailPath?: (countryCode: string) => string;
  getDetailGamePath?: (game: IMobileStoreRankTopGlobalCompareRankGame) => string;
}

const GlobalCompareTable: React.FC<IGlobalCompareTableProps> = (props) => {
  // const { records, selectable } = props;
  const {
    isLoading,
    isError,
    total,
    records: recordsOuter,
    pageIndex,
    pageSize,
    pageSizeOptions = [10, 20],
    onPagingChange,
    onRefresh,
    onCountryClick,
    onGameClick,
    getCountryDetailPath,
    getDetailGamePath,
  } = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  // 总数据是否小于最小页码大小，即最小页码时，是否能一页展示完所有数据
  const isOnlyOnePage = total / Math.min(...pageSizeOptions) <= 1;

  const [activeUnifiedId, setActiveUnifiedId] = useState('');

  // 压缩游戏图片
  const records = useMemo<IMobileStoreRankTopGlobalCompareRank[]>(() => {
    return handleGameRanks(recordsOuter);
  }, [recordsOuter]);

  /**
   * 计算表格状态，加载中？ 加载异常？ 空数数据？ 正常情况返回null，根据表格状态判断渲染
   */
  const tableState = useMemo(() => {
    if (isLoading) return TABLE_STATE.LOADING;
    if (isError) return TABLE_STATE.ERROR;
    if (!records?.length) return TABLE_STATE.EMPTY;
    return null;
  }, [isLoading, isError, records]);

  const ranksColumns = useMemo<TableColumn<IMobileStoreRankTopGlobalCompareRank>[]>(() => {
    return Array.from({ length: 20 }).map((item, index) => {
      const order = index + 1;
      return {
        key: `rank-${order}`,
        header: `#${order}`,
        align: 'center',
        // width: 132, // 旧情报的宽度
        width: 160, // 新风格重构给的宽度
        // render: (row, rowKey: string, rowIndex: number) => {
        render: (row) => {
          const gameRanks = row?.game_rank;
          if (!Array.isArray(gameRanks)) return null;
          const cvm = gameRanks[index];
          const cvmId = cvm?.unified_id ?? '';
          return (
            <ARightClickToJump href={getDetailGamePath?.(cvm)} style={{ display: 'block' }}>
              <CompareGameBox
                isActive={cvmId !== '' && activeUnifiedId === cvmId}
                isShowTopInfo={records?.length > 1}
                data={cvm}
                onMouseEnter={() => {
                  setActiveUnifiedId(cvmId);
                }}
                onMouseLeave={() => {
                  setActiveUnifiedId('');
                }}
                onClick={(e) => {
                  e?.stopPropagation();
                  e?.preventDefault();
                  onGameClick?.(cvm, e);
                }}
              />
            </ARightClickToJump>
          );
        },
      };
    });
  }, [activeUnifiedId, onGameClick]);

  const columns = useMemo<TableColumn<IMobileStoreRankTopGlobalCompareRank>[]>(() => {
    return [
      {
        key: 'Country',
        header: (
          <Bubble
            content={formatMessage({
              id: 'dbi_store_rank_mobile_global_compare_country_column_tips',
            })}
            dark
          >
            <span className="tea-text-overflow">{formatMessage({ id: 'dbi_countries' })}</span>
          </Bubble>
        ),
        width: 220,
        fixed: 'left',
        render: (cvm) => (
          <ARightClickToJump
            href={getCountryDetailPath?.(cvm?.market)}
            className={'dbi-globalcompare-table-link'}
            style={{ display: 'block !important' }}
          >
            <div
              className="dbi-countrylabel"
              onClick={(e) => {
                e?.stopPropagation();
                e?.preventDefault();
                onCountryClick?.(cvm?.market, e);
              }}
            >
              <img className="dbi-countrylabel-icon" src={getCountryFlag(cvm?.market)} />
              {/* 截断时显示bubble */}
              {/* <Bubble content={cvm.market_name} dark> */}
              <span className="dbi-countrylabel-text">{cvm?.market_name}</span>
              {/* </Bubble> */}
            </div>
          </ARightClickToJump>
        ),
      },
      ...ranksColumns,
    ];
  }, [locale, ranksColumns, onCountryClick]);

  const handleRefresh = () => {
    onRefresh?.();
  };

  if (tableState !== null) {
    return <TableState state={tableState} columns={columns} onRefresh={handleRefresh} />;
  }

  // 表格一行仅显示10个游戏示意，如需补充开发可参考已有列添加
  return (
    <Table
      className="dbi-globalcompare-table dbi-table-sticky"
      records={records}
      columns={columns}
      addons={[
        pageable({
          recordCount: total,
          stateText: `${total} items in total`,
          pageIndex,
          pageSize,
          pageSizeOptions,
          // 最小页码时，一页能展示完所有数据时，只显示 总数居条数提示，分页器其他部分不展示
          pageSizeVisible: !isOnlyOnePage,
          pageIndexVisible: !isOnlyOnePage,
          jumpVisible: !isOnlyOnePage,
          endJumpVisible: !isOnlyOnePage,
          onPagingChange,
        }),
        scrollable({
          minWidth: 1300,
        }),
      ]}
    />
  );
};

export default memo(GlobalCompareTable);
